import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { LOGIN, REGISTER, FORGOT_PASSWORD } from "@/constants/testIds";
import { Activity, Eye, EyeOff, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [tab, setTab]           = useState("login");   // "login" | "register"
  const [view, setView]         = useState("auth");    // "auth" | "forgot" | "forgot-sent"
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const { login, register }     = useAuth();
  const navigate                = useNavigate();

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm,   setRegForm]   = useState({ name: "", email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(loginForm.email, loginForm.password);
    setLoading(false);
    if (result.success) navigate("/dashboard");
    else setError(result.error);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!regForm.name.trim()) { setError("Name is required"); return; }
    setLoading(true);
    const result = await register(regForm.name, regForm.email, regForm.password);
    setLoading(false);
    if (result.success) navigate("/dashboard");
    else setError(result.error);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setView("forgot-sent");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setError("");
    setForgotEmail(loginForm.email); // pre-fill if user already typed email
    setView("forgot");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 bg-blue-900 flex-col justify-between p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=900&q=60')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Activity className="text-white" size={20} />
            </div>
            <span className="text-white text-2xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              LabReport AI
            </span>
          </div>

          <h1 className="text-4xl font-light text-white leading-tight mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Turn blood tests into<br />
            <span className="font-semibold">actionable insights</span>
          </h1>
          <p className="text-blue-200 text-base leading-relaxed max-w-sm">
            Upload any blood test PDF and get a comprehensive report with plain-language explanations and medical recommendations — in seconds.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { val: "99%", label: "Accuracy" },
            { val: "<30s", label: "Analysis time" },
            { val: "50+", label: "Blood markers" },
          ].map((s) => (
            <div key={s.label} className="bg-white/10 backdrop-blur rounded-lg p-4">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{s.val}</p>
              <p className="text-blue-200 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-blue-900 flex items-center justify-center">
              <Activity className="text-white" size={16} />
            </div>
            <span className="text-slate-900 text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>LabReport AI</span>
          </div>

          {/* ── Forgot password form ── */}
          {view === "forgot" && (
            <div>
              <button
                data-testid={FORGOT_PASSWORD.backButton}
                onClick={() => { setView("auth"); setError(""); }}
                className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-sm mb-8 transition-colors">
                <ArrowLeft size={15} /> Back to sign in
              </button>

              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-6">
                <Mail size={22} className="text-blue-900" />
              </div>

              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Forgot your password?
              </h2>
              <p className="text-slate-500 text-sm mb-7">
                Enter your email and we'll send you a link to reset your password.
              </p>

              {error && (
                <div data-testid={FORGOT_PASSWORD.errorMessage} className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                  <input
                    data-testid={FORGOT_PASSWORD.emailInput}
                    type="email" required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <button
                  data-testid={FORGOT_PASSWORD.submitButton}
                  type="submit" disabled={loading}
                  className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </form>
            </div>
          )}

          {/* ── Email sent confirmation ── */}
          {view === "forgot-sent" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                Check your email
              </h2>
              <p className="text-slate-500 text-sm mb-2 leading-relaxed">
                We've sent a password reset link to:
              </p>
              <p data-testid={FORGOT_PASSWORD.sentEmailDisplay} className="font-semibold text-slate-800 text-sm mb-7">
                {forgotEmail}
              </p>
              <p className="text-slate-400 text-xs mb-8 leading-relaxed">
                The link expires in 1 hour. Check your spam folder if you don't see it.
              </p>
              <button
                data-testid={FORGOT_PASSWORD.backToLoginButton}
                onClick={() => { setView("auth"); setTab("login"); setError(""); }}
                className="text-blue-900 text-sm font-medium hover:underline flex items-center gap-1.5 mx-auto">
                <ArrowLeft size={14} /> Back to sign in
              </button>
            </div>
          )}

          {/* ── Login / Register tabs ── */}
          {view === "auth" && (
            <>
              {/* Tabs */}
              <div className="flex bg-slate-100 rounded-lg p-1 mb-8">
                <button
                  data-testid="login-tab-button"
                  onClick={() => { setTab("login"); setError(""); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  Sign in
                </button>
                <button
                  data-testid="register-tab-button"
                  onClick={() => { setTab("register"); setError(""); }}
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === "register" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  Create account
                </button>
              </div>

              {error && (
                <div data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5">
                  {error}
                </div>
              )}

              {tab === "login" ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Welcome back</h2>
                  <p className="text-slate-500 text-sm -mt-3">Sign in to access your reports</p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                    <input
                      data-testid={LOGIN.emailInput}
                      type="email" required
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-medium text-slate-700">Password</label>
                      <button
                        type="button"
                        data-testid={FORGOT_PASSWORD.triggerLink}
                        onClick={openForgot}
                        className="text-xs text-blue-900 hover:underline font-medium">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        data-testid={LOGIN.passwordInput}
                        type={showPw ? "text" : "password"} required
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    data-testid={LOGIN.submitButton}
                    type="submit" disabled={loading}
                    className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? "Signing in..." : "Sign in"}
                  </button>

                  <p className="text-center text-sm text-slate-500">
                    Don't have an account?{" "}
                    <button type="button" data-testid={LOGIN.registerLink} onClick={() => setTab("register")}
                      className="text-blue-900 font-medium hover:underline">
                      Create one
                    </button>
                  </p>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-5">
                  <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Create your account</h2>
                  <p className="text-slate-500 text-sm -mt-3">Start analyzing blood tests in minutes</p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name</label>
                    <input
                      data-testid={REGISTER.nameInput}
                      type="text" required
                      value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      placeholder="Dr. Jane Smith"
                      className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                    <input
                      data-testid={REGISTER.emailInput}
                      type="email" required
                      value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        data-testid={REGISTER.passwordInput}
                        type={showPw ? "text" : "password"} required minLength={6}
                        value={regForm.password}
                        onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                        placeholder="Min. 6 characters"
                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    data-testid={REGISTER.submitButton}
                    type="submit" disabled={loading}
                    className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? "Creating account..." : "Create account"}
                  </button>

                  <p className="text-center text-sm text-slate-500">
                    Already have an account?{" "}
                    <button type="button" data-testid={REGISTER.loginLink} onClick={() => setTab("login")}
                      className="text-blue-900 font-medium hover:underline">
                      Sign in
                    </button>
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
